import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Wallet,
  House,
  Receipt,
  Tag,
  Trash,
  Bell,
  SignOut,
  User,
  List,
  X,
  CaretDown,
  Warning,
  Clock,
  CheckCircle,
  DownloadSimple,
  UploadSimple,
  Database,
  CloudArrowUp,
  CloudCheck,
  CreditCard,
  ChartBar,
  Eraser
} from "@phosphor-icons/react";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: House },
  { path: "/bills", label: "Contas", icon: Receipt },
  { path: "/categories", label: "Categorias", icon: Tag },
  { path: "/payment-methods", label: "Formas", icon: CreditCard },
  { path: "/reports", label: "Relatórios", icon: ChartBar },
  { path: "/trash", label: "Lixeira", icon: Trash },
];

// Auto backup interval in milliseconds (5 minutes)
const AUTO_BACKUP_INTERVAL = 5 * 60 * 1000;

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [lastBackup, setLastBackup] = useState(null);
  const [backupStatus, setBackupStatus] = useState('idle'); // idle, saving, saved
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetType, setResetType] = useState('period'); // 'period' or 'all'
  const [resetStartDate, setResetStartDate] = useState('');
  const [resetEndDate, setResetEndDate] = useState('');
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (resetConfirmText !== 'ZERAR') {
      toast.error('Digite "ZERAR" para confirmar');
      return;
    }
    setResetting(true);
    try {
      const payload = resetType === 'all' 
        ? { reset_all: true }
        : { start_date: resetStartDate, end_date: resetEndDate };
      const response = await api.post('/bills/reset', payload);
      toast.success(response.data.message);
      setResetDialogOpen(false);
      setResetConfirmText('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao zerar dados');
    } finally {
      setResetting(false);
    }
  };

  // Load last backup time from localStorage
  useEffect(() => {
    const savedBackupTime = localStorage.getItem('lastAutoBackupTime');
    if (savedBackupTime) {
      setLastBackup(new Date(savedBackupTime));
    }
  }, []);

  // Auto backup function
  const performAutoBackup = useCallback(async () => {
    try {
      setBackupStatus('saving');
      const response = await api.get("/backup/export");
      
      // Save to localStorage as JSON string
      const backupData = response.data;
      localStorage.setItem('autoBackupData', JSON.stringify(backupData));
      
      const now = new Date();
      localStorage.setItem('lastAutoBackupTime', now.toISOString());
      setLastBackup(now);
      setBackupStatus('saved');
      
      // Reset status after 3 seconds
      setTimeout(() => setBackupStatus('idle'), 3000);
      
      console.log('Auto backup completed:', now.toLocaleString('pt-BR'));
    } catch (error) {
      console.error('Auto backup failed:', error);
      setBackupStatus('idle');
    }
  }, []);

  // Set up auto backup interval
  useEffect(() => {
    // Perform initial backup after 30 seconds
    const initialBackup = setTimeout(() => {
      performAutoBackup();
    }, 30000);

    // Set up recurring backup
    const backupInterval = setInterval(() => {
      performAutoBackup();
    }, AUTO_BACKUP_INTERVAL);

    return () => {
      clearTimeout(initialBackup);
      clearInterval(backupInterval);
    };
  }, [performAutoBackup]);

  // Restore from local backup
  const restoreFromLocalBackup = async () => {
    const backupData = localStorage.getItem('autoBackupData');
    if (!backupData) {
      toast.error('Nenhum backup local encontrado');
      return;
    }

    try {
      const data = JSON.parse(backupData);
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      const file = new File([blob], 'local_backup.json', { type: 'application/json' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post("/backup/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Backup local restaurado! ${response.data.imported_bills} contas, ${response.data.imported_categories} categorias.`);
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao restaurar backup local');
    }
  };

  // Format last backup time
  const formatLastBackup = () => {
    if (!lastBackup) return 'Nunca';
    const now = new Date();
    const diff = Math.floor((now - lastBackup) / 1000 / 60); // minutes
    if (diff < 1) return 'Agora';
    if (diff < 60) return `${diff} min atrás`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours}h atrás`;
    return lastBackup.toLocaleDateString('pt-BR');
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get("/notifications/due");
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleExportBackup = async () => {
    try {
      const response = await api.get("/backup/export", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `backup_financeiro_${date}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Backup exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar backup");
    }
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post("/backup/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Backup importado! ${response.data.imported_bills} contas, ${response.data.imported_categories} categorias.`);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar backup");
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const urgentCount = notifications.filter(n => n.type === "overdue" || n.type === "today").length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case "overdue": return <Warning className="text-destructive" weight="fill" />;
      case "today": return <Clock className="text-yellow-600" weight="fill" />;
      case "tomorrow": return <CheckCircle className="text-primary" weight="fill" />;
      default: return <Bell />;
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Hidden file input for backup import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportBackup}
        accept=".json"
        className="hidden"
        data-testid="backup-import-input"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary-foreground" weight="fill" />
              </div>
              <span className="font-heading font-extrabold text-xl tracking-tight text-primary hidden sm:block">
                Financeiro
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                  >
                    <Icon size={18} weight={isActive ? "fill" : "regular"} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notifications-button"
                  >
                    <Bell size={20} />
                    {urgentCount > 0 && (
                      <span className="notification-badge">{urgentCount}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="px-3 py-2 border-b">
                    <h4 className="font-heading font-semibold">Notificações</h4>
                  </div>
                  <ScrollArea className="h-72">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        Nenhuma conta pendente próxima
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start gap-3 px-3 py-3 hover:bg-muted/50 border-b last:border-0"
                        >
                          <div className="mt-0.5">{getNotificationIcon(notif.type)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              R$ {notif.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} • {notif.due_date}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="gap-2"
                    data-testid="user-menu-button"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center relative">
                      <User size={16} className="text-primary" weight="fill" />
                      {backupStatus === 'saving' && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
                      )}
                      {backupStatus === 'saved' && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full" />
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    <CaretDown size={14} className="text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {/* Auto Backup Status */}
                  <div className="px-3 py-2 bg-muted/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {backupStatus === 'saving' ? (
                          <CloudArrowUp size={16} className="text-yellow-600 animate-pulse" />
                        ) : (
                          <CloudCheck size={16} className="text-green-600" />
                        )}
                        <span className="text-xs font-medium">Backup Automático</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {backupStatus === 'saving' ? 'Salvando...' : formatLastBackup()}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Salvo automaticamente a cada 5 minutos
                    </p>
                  </div>
                  
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Backup Manual</p>
                    <DropdownMenuItem 
                      onClick={handleExportBackup}
                      className="cursor-pointer"
                      data-testid="export-backup-button"
                    >
                      <DownloadSimple size={16} className="mr-2" />
                      Baixar Backup (JSON)
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer"
                      data-testid="import-backup-button"
                    >
                      <UploadSimple size={16} className="mr-2" />
                      Importar de Arquivo
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={restoreFromLocalBackup}
                      className="cursor-pointer"
                      data-testid="restore-local-backup-button"
                    >
                      <Database size={16} className="mr-2" />
                      Restaurar Backup Local
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={performAutoBackup}
                      className="cursor-pointer"
                      data-testid="force-backup-button"
                    >
                      <CloudArrowUp size={16} className="mr-2" />
                      Fazer Backup Agora
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setResetDialogOpen(true)}
                    className="text-amber-600 focus:text-amber-600 cursor-pointer"
                    data-testid="reset-data-button"
                  >
                    <Eraser size={16} className="mr-2" />
                    Zerar Dados
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="logout-button"
                  >
                    <SignOut size={16} className="mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={20} /> : <List size={20} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background/95 backdrop-blur">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Reset Data Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Eraser size={20} />
              Zerar Dados
            </DialogTitle>
            <DialogDescription>
              Remove contas e pagamentos. Categorias e formas de pagamento são mantidas.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button
                variant={resetType === 'period' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setResetType('period')}
              >
                Por Período
              </Button>
              <Button
                variant={resetType === 'all' ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => setResetType('all')}
              >
                Zerar Tudo
              </Button>
            </div>

            {resetType === 'period' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Data Inicial</Label>
                  <Input 
                    type="date" 
                    value={resetStartDate} 
                    onChange={(e) => setResetStartDate(e.target.value)}
                    data-testid="reset-start-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Final</Label>
                  <Input 
                    type="date" 
                    value={resetEndDate} 
                    onChange={(e) => setResetEndDate(e.target.value)}
                    data-testid="reset-end-date"
                  />
                </div>
              </div>
            )}

            {resetType === 'all' && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive font-medium">
                  Todas as contas e pagamentos serão removidos permanentemente.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">
                Digite <strong>ZERAR</strong> para confirmar
              </Label>
              <Input 
                value={resetConfirmText} 
                onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                placeholder="ZERAR"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetDialogOpen(false); setResetConfirmText(''); }}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReset} 
              disabled={resetting || resetConfirmText !== 'ZERAR' || (resetType === 'period' && (!resetStartDate || !resetEndDate))}
              data-testid="reset-confirm-button"
            >
              {resetting ? 'Zerando...' : 'Confirmar Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
